import AssignModalTwo from '@/components/assignWizard/AssignModal';
import AssignedTab from '@/components/assignWizard/AssignedTab';
import { Button } from '@/components/button/Button';
import { AdvancePermission } from '@/models/AdvancePermission';
import AdvancePermissionColumn from '@/models/ColumnDefinitions/AdvancePermissionColumn';
import { ListQuery } from '@/models/ListQuery';
import { PaginationModel } from '@/models/Pagination';
import AdvancePermissionSearch from '@/models/search/AdvancePermissionSearch';
import {assignUnassignAdvancePermission, getAdvancePermissions, getUserAdvancePermissions} from '@/services/advancePermissionService';
import { getAllowedCapabilityIdsForProfile } from '../../../constants/user-capabilities-constants';
import { getUserDataFiltersByLoginId } from '@/services/userPreferenceService';
import { RouterRootState } from 'connected-react-router';
import React, { FC, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import { useDialog } from '../../../components/dialog/DialogContext';
import { Topic } from '../../../components/item-content-two/Topic';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import DataFilters from '../../../components/login/dataFilters';
import { LoginCard } from "../../../components/login/loginCard";
import { LoginDetails } from "../../../components/login/loginDetails";
import PasswordDetails from "../../../components/login/passwordDetails";
import { isSysdbaAccount } from '../../../constants/logins-account-types';
import { isAdminOrAbove } from '../../../constants/preset-constants';
import { LOGIN_TYPE_MANUAL } from '../../../constants/user-login-constants';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { TabSummary } from '../../../models/TabSummary';
import { UserLogin } from '../../../models/UserLogin';
import { getLoginById, getLoginNamesWithPagination, storeLogin } from '../../../services/loginService';
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { isAuthLogin, isAuthWithGroupsLogin, isLdapLogin } from '../../../services/utilities/loginsUtils';
import { IAppState } from "../../../store/types";

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const initialItem: UserLogin = {
    name: '',
    displayName: '',
    idNumber: '',
    siteFilter: '',
    loginTypeId: LOGIN_TYPE_MANUAL,
    origLoginTypeId: LOGIN_TYPE_MANUAL,
    isChangingLoginTypes: false,
    master: '',
    masterId: 0,
    site: '',
    siteId: 0,
    profile: '',
    profileId: 0,
    securityPolicy:'',
    securityPolicyId: 0,
    id: 0,
    lastName: '',
    firstName: '',
    ulPassword: '',
    mfaEnabled: false,
    userLoginEmail: '',
    userOid:''
}

const allItemsPath = '/access-control/operators/logins';
const itemPath = '/access-control/operators/login/';

const Login: FC = () => {
    const { translate } = useLocale();
    const [currentItem, setCurrentItem] = useState<UserLogin>(initialItem);
    const [isNew, setIsNew] = useState(false);
    const initialTabs: TabSummary[] = [
        { id: 1, label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { id: 2, label: translate.byKey(TranslationKeys.security), badge: 0, showInitial: isNew ? false : true, isActive: true },
    ];
    const [isLdapMode, setIsLdapMode] = useState<boolean>(false);
    const [isAuthMode, setIsAuthMode] = useState<boolean>(false);
    const [isAuthWithGroupsMode, setIsAuthModeWithGroups] = useState<boolean>(false);
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const [itemId, setItemId] = useState(pageId);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const userLoginDetails = useSelector((state: IAppState) => (state.userInfo));
    const dialog = useDialog();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const [showAdvancePermissionModal, setShowAdvancePermissionModal] = useState<boolean>(false);
    const [userAdvancePermissions, setUserAdvancePermissions] = useState<AdvancePermission[]>([]);

    const reloadPage = useCallback(async () => {
        if (!itemId) {
            setCurrentItem(initialItem);
            setTabs([initialTabs[0]]);
            return;
        }
        try {
            let login;
            if (itemId) {
                login = await getLoginById(itemId);
                const isLdapMode = isLdapLogin(login.loginTypeId);
                setIsLdapMode(isLdapMode);

                const isAuthMode = isAuthLogin(login.loginTypeId);
                setIsAuthMode(isAuthMode);

                const isAuthWithGroupsMode = isAuthWithGroupsLogin(login.loginTypeId);
                setIsAuthModeWithGroups(isAuthWithGroupsMode);

                setSelectedItem({label: login.name, value: login.id.toString(), isDefault: login.isDefault});
                if (isLdapMode) {
                    setCurrentItem({ ...login, uniqueIdentifier: login.name, profileId: login.profileId.toString(), commonName: login.ulCommonName});
                }
                else if (isAuthMode || isAuthWithGroupsMode) {
                    setCurrentItem({ ...login, userLoginEmail: login.userEmail, idGroupsEnabled:login.idGroupsEnabled, ulUserName: login.name, uniqueIdentifierOid: login.userOid, profileId: login.profileId.toString(), commonName: login.ulCommonName});
                }
                else {
                    setCurrentItem({...login, securityPolicyId: login.securityPolicyId.toString(), securityPolicy: login.securityPolicy, profileId: login.profileId.toString(), ulPassword: login.ulPassword});
                }

                /* Data Filters tab is only shown when all of the following is true:
                   - User is SysAdmin or System Owner, and (this allows both System Owners and Site Admins to manage their own sites)
                   - Login being viewed is not the SYSDBA account, and  (this prevents SYSDBA permissions from being downgraded)
                   - User is not viewing their own account. (this prevents users from upgrading their own permissions)
                */
                const isOwnAccount = parseInt(itemId) === parseInt(userLoginDetails.id);
                if (isAdminOrAbove(userLoginDetails.profileId) && !isSysdbaAccount(parseInt(itemId)) && !isOwnAccount) {
                    const dataFiltersCount = await getUserDataFiltersByLoginId(itemId);
                    const updatedTabs =[
                        { id: 1, label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
                        { id: 2, label: translate.byKey(TranslationKeys.security), badge: 0, showInitial: isNew ? false : true, isActive: true },
                        { id: 3, label: translate.byKey(TranslationKeys.dataFilters), badge: dataFiltersCount.pagination.totalCount, showInitial: true, isActive: true },
                        { id: 4, label: translate.byKey(TranslationKeys.advancedPermissions), badge: 0, showInitial: true, isActive: true },
                    ];
                    if (isAuthMode || isAuthWithGroupsMode) {
                        // For Entra logins, we want to hide the Security tab as it is not relevant, but we still want to show the badge count for data filters if there are any.
                        updatedTabs[1].isActive = false;
                    }
                    setTabs(updatedTabs);
                }
                else {
                    setTabs(initialTabs);
                }
            }
            else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[0]]);
            }
            setBreadcrumb([
                { value: '/access-control/operators', displayName: translate.byKey(TranslationKeys.operators), disableClick: true },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.logins) },
                { value: itemPath + `${itemId}`, displayName: `${login?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }, [itemId, userLoginDetails.profileId, translate, dialog, history]);

    useEffect(() => {
        const id = getIdFromPath(history.location.pathname);
        setIsNew(!id);
        setItemId(id);
    }, [history.location.pathname]);

    useEffect(() => {
        reloadPage();
    }, [reloadPage]);

    const onDataFilterSave = (index) => {
        setTabs(updateTabBadgeTotal(index, tabs[index].badge + 1, tabs));
    }

    const onDataFilterDeleteUpdateBadge = (index) => {
        setTabs(updateTabBadgeTotal(index, tabs[index].badge - 1, tabs));
    }

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        newTabs[tabIndex].isActive = true; // Set tab to active so we can show it
        return newTabs;
    };

    const topic: Topic = {
        accessor: 'ulUsername',
        allItemsPath,
        itemPath,
        title: translate.byKey(TranslationKeys.logins),
    }

    const tabContentList: ContentWrapper[] = [
        {
            content: <LoginDetails />
        },
        {
            content: <PasswordDetails itemId={itemId} isLdapLoginType={isLdapMode} isAuthLoginType={isAuthMode} isAuthWithGroupsLoginType={isAuthWithGroupsMode}/>
        },
        {
            content: <DataFilters itemId={itemId} onDeleteFilter={(deleteRecord) => { if (deleteRecord) onDataFilterDeleteUpdateBadge(2) }} onSave={(createNew) => { if (createNew) onDataFilterSave(2) }} />
        },
        {
            content: <AssignedTab
                key={new Date().getTime()} // Force remount when itemId changes to ensure data is refreshed
                column={AdvancePermissionColumn}
                searchColumn={AdvancePermissionSearch}
                id={itemId}
                dataMethod={(listParameters?: ListQuery) => getUserAdvancePermissions(itemId, listParameters)}
                title={translate.byKey(TranslationKeys.assignedPermissions)}>
                <Button variant="submit" shape="rounded" id="manageButton" className="py-1 px-5" onClick={() => setShowAdvancePermissionModal(true)}>
                    {translate.byKey(TranslationKeys.manage)}
                </Button>
            </AssignedTab>
        }
    ]

    const getTabContent = (index?: number) => {
        const tabIndex = index ?? 0;

        // Check if both tabs and tabContentList have content at the given index
        if (!tabs[tabIndex] || !tabContentList[tabIndex]) {
            return tabContentList[tabIndex - 1].content;
        }
        // Check if the current tab is active
        else if (itemId && tabs[tabIndex].isActive) {
            const selectedTabContent = tabContentList[tabIndex];
            return selectedTabContent.content;
        }

        return tabContentList[0].content;
    };

    const saveLogin = (values) => {
        let userName = values.name.trim();

        if (isLdapLogin(values.loginTypeId)) {
            userName = values.uniqueIdentifier
        }
        else if (isAuthLogin(values.loginTypeId) || isAuthWithGroupsLogin(values.loginTypeId)) {
            userName = values.userLoginEmail
        }

        const newLogin = {
            ...currentItem,
            id: +itemId,
            username: userName,
            ulPassword: !isLdapLogin(values.loginTypeId) && values.ulPassword,
            siteFilter: '',
            ulLicense: 0,
            ulReset: 1,
            profileId: values.profileId,
            securityPolicyId: !isLdapLogin(values.loginTypeId) && values.securityPolicyId,
            masterId: values.masterId,
            ulConfirmPassword: !isLdapLogin(values.loginTypeId) && values.ulConfirmPassword,
            siteId: itemId ? values.siteId : siteId,
            loginTypeId: values.loginTypeId,
            ulCommonName:  isLdapLogin(values.loginTypeId) && values.ulCommonName,
            mfaEnabled: values.mfaEnabled,
            userLoginIod: (isAuthLogin(values.loginTypeId) || isAuthWithGroupsLogin(values.loginTypeId)) && values.uniqueIdentifierOid,
            idGroupsEnabled: (isAuthLogin(values.loginTypeId) && values.idGroupsEnabled) || isAuthWithGroupsLogin(values.loginTypeId),
            userLoginEmail: (isAuthLogin(values.loginTypeId) || isAuthWithGroupsLogin(values.loginTypeId)) && values.userLoginEmail,
        };

        const storeMethod = storeLogin;
        saveItem(setBusy, storeMethod, newLogin, isNew, setItemId, history);
    }

    const saveItem = (setBusy, storeMethod, item, createNewItem, setItemId, history) => {
        setBusy(true);

        // User is changing their login type.
        if (item.loginTypeId !== item.origLoginTypeId) {
            item.isChangingLoginTypes = true;
        }

        storeMethod(item, createNewItem)
            .then(async (value) => {
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                reloadPage();
                toast.success(translate.byKey(TranslationKeys.loginSaved));
                getTabContent()
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
        setBusy(false);
    }

    const getAssignedAdvancePermission = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setBusyLoader: React.Dispatch<React.SetStateAction<boolean>>) => {
        try {
            setBusyLoader(true);
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: AdvancePermission[] = []
            do {
                const userAdvancePermissions =  await getUserAdvancePermissions(itemId);
                ids = ids.concat(userAdvancePermissions.rows.map(permission => permission.settingTypeId.toString()))
                rows = rows.concat(userAdvancePermissions.rows);
                pagination = userAdvancePermissions.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);
            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
            setUserAdvancePermissions(rows);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
        finally {
            setBusyLoader(false);
        }
    }

    const assignMembers = async (toBeAssigned: string[], toBeUnssigned: string[]) => {
        const toBeUnassignedUserPreferences: string[] = toBeUnssigned.map(id => userAdvancePermissions.find(permission => permission.settingTypeId.toString() === id)).filter(permission => permission !== undefined).map(permission => permission.id.toString());
        assignUnassignAdvancePermission(toBeUnassignedUserPreferences, toBeAssigned, itemId).then(() => {
            toast.success(translate.byKey(TranslationKeys.success));
            reloadPage();
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        }).finally(() => {
            setBusy(false);
        });
    }

    return <>
        <TopicContextProvider dataMethod={listparameters => getLoginNamesWithPagination(listparameters, parseInt(user.profileId))}
            itemId={itemId} topic={topic} selectedItem={selectedItem} >
            <ItemContentTabTwo>
                <SimpleItemDetails
                    key={itemId}
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveLogin}
                    tabs={tabs}
                    itemPath={itemPath}
                    initialValues={currentItem}>
                    <LoginCard currentItem={currentItem} setIsLdapMode={setIsLdapMode} setIsAuthMode={setIsAuthMode} setIsAuthWithGroupsMode={setIsAuthModeWithGroups} />
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
        <AssignModalTwo
            key={`assignModalForAdvancePermission${itemId}`}
            tableColumns={AdvancePermissionColumn}
            searchColumns={AdvancePermissionSearch}
            isOpen={showAdvancePermissionModal}
            onClose={() => setShowAdvancePermissionModal(false)}
            assigningTitle={translate.byKey(TranslationKeys.assignAdvancedPermissions)}
            modalTitle={translate.byKey(TranslationKeys.permissions)}
            getAssignedItems={getAssignedAdvancePermission}
            dataMethod={(listParameter?: ListQuery) => {
                const allowedIds = getAllowedCapabilityIdsForProfile(Number(currentItem.profileId));
                return getAdvancePermissions(listParameter).then(result => ({
                    ...result,
                    rows: allowedIds
                        ? result.rows.filter(p => allowedIds.has(p.settingTypeId))
                        : result.rows,
                }));
            }}
            handleSubmit={assignMembers} />
    </>
}

export default Login;
